import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useUserNames } from '../context/UserNamesContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Rocket, FileSignature, CheckCircle2, Circle, Trash2, Plus, Upload, Download,
  Send, ClipboardList, UserCheck, Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTRACT_BADGE = {
  none: { label: 'No contract', cls: 'bg-muted text-muted-foreground' },
  sent: { label: 'Contract sent', cls: 'bg-amber-100 text-amber-700' },
  signed: { label: 'Contract signed', cls: 'bg-green-100 text-green-700' },
};

const AgencyOnboarding = ({ agencyId, onAgencyChange }) => {
  const { resolveName } = useUserNames();
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialogs
  const [contractOpen, setContractOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [contractForm, setContractForm] = useState({
    template_id: '', contact_name: '', contact_email: '', message: '',
    expires_at: '', reminders_enabled: false,
  });
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', assignee_user_id: '', due_date: '', reminders_enabled: false,
  });
  const [sending, setSending] = useState(false);
  const [applyPackId, setApplyPackId] = useState('');

  const load = async () => {
    try {
      const [ob, tpl, usr, pk] = await Promise.all([
        axios.get(`${API}/agency-profiles/${agencyId}/onboarding`),
        axios.get(`${API}/esignature/templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/agency-onboarding/packs`).catch(() => ({ data: [] })),
      ]);
      setData(ob.data);
      setTemplates(Array.isArray(tpl.data) ? tpl.data : (tpl.data?.templates || []));
      setUsers(Array.isArray(usr.data) ? usr.data : (usr.data?.users || []));
      setPacks(pk.data || []);
      setContractForm((f) => ({
        ...f,
        contact_name: ob.data.primary_contact?.name || '',
        contact_email: ob.data.primary_contact?.email || '',
      }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agencyId]);

  const userName = (u) =>
    `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || u.email || 'User';

  const sendContract = async () => {
    if (!contractForm.template_id) { toast.error('Pick a template'); return; }
    if (!contractForm.contact_email) { toast.error('Enter the agency contact email'); return; }
    setSending(true);
    try {
      // 1) save contact
      await axios.put(`${API}/agency-profiles/${agencyId}/contact`, {
        name: contractForm.contact_name, email: contractForm.contact_email,
      });
      // 2) create e-sign request (external signer = agency contact), linked to agency
      const createRes = await axios.post(`${API}/esignature/requests`, {
        template_id: contractForm.template_id,
        name: `${data.agency_name} — Partnership Contract`,
        message: contractForm.message || null,
        agency_id: agencyId,
        expires_days: 30,
        signers: [{
          type: 'external',
          name: contractForm.contact_name || 'Agency Contact',
          email: contractForm.contact_email,
          role: 'Agency',
          order: 0,
        }],
      });
      const reqId = createRes.data?.signing_request?.id;
      // 3) send it
      await axios.post(`${API}/esignature/requests/${reqId}/send`, {});
      // 4) record on agency
      await axios.post(`${API}/agency-profiles/${agencyId}/contract`, {
        esign_request_id: reqId,
        expires_at: contractForm.expires_at || null,
        reminders_enabled: contractForm.reminders_enabled,
        reminder_days: [30, 7],
      });
      toast.success('Contract sent for signature');
      setContractOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send contract');
    } finally {
      setSending(false);
    }
  };

  const convertToActive = async () => {
    if (!window.confirm(`Convert "${data.agency_name}" to an Active agency?`)) return;
    try {
      await axios.post(`${API}/agency-profiles/${agencyId}/convert-to-active`);
      toast.success('Agency is now Active');
      load();
      onAgencyChange && onAgencyChange();
    } catch (e) {
      toast.error('Failed to convert agency');
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/agency-profiles/${agencyId}`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      load();
      onAgencyChange && onAgencyChange();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const applyPack = async () => {
    if (!applyPackId) return;
    try {
      const res = await axios.post(`${API}/agency-profiles/${agencyId}/onboarding/apply-pack/${applyPackId}`);
      toast.success(`Added ${res.data.added} tasks`);
      setApplyPackId('');
      load();
    } catch (e) { toast.error('Failed to apply pack'); }
  };

  const addTask = async () => {
    if (!taskForm.title.trim()) { toast.error('Task title required'); return; }
    try {
      const assignee = users.find((u) => u.id === taskForm.assignee_user_id);
      await axios.post(`${API}/agency-profiles/${agencyId}/onboarding/tasks`, {
        title: taskForm.title,
        description: taskForm.description,
        assignee_user_id: taskForm.assignee_user_id || null,
        assignee_name: assignee ? userName(assignee) : null,
        due_date: taskForm.due_date || null,
        reminders_enabled: taskForm.reminders_enabled,
      });
      toast.success('Task added');
      setTaskOpen(false);
      setTaskForm({ title: '', description: '', assignee_user_id: '', due_date: '', reminders_enabled: false });
      load();
    } catch (e) { toast.error('Failed to add task'); }
  };

  const toggleTask = async (task) => {
    try {
      await axios.put(`${API}/agency-profiles/${agencyId}/onboarding/tasks/${task.id}`, {
        status: task.status === 'done' ? 'pending' : 'done',
      });
      load();
    } catch (e) { toast.error('Failed to update task'); }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/agency-profiles/${agencyId}/onboarding/tasks/${taskId}`);
      load();
    } catch (e) { toast.error('Failed to delete task'); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/agency-profiles/${agencyId}/documents?name=${encodeURIComponent(file.name)}&category=other`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      load();
    } catch (err) { toast.error('Upload failed'); }
    e.target.value = '';
  };

  const downloadDoc = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/agency-profiles/${agencyId}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name || doc.name || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error('Download failed'); }
  };

  const deleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/agency-profiles/${agencyId}/documents/${docId}`);
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading || !data) {
    return <Card className="p-6" data-testid="agency-onboarding-loading">Loading onboarding…</Card>;
  }

  const tasks = data.onboarding_tasks || [];
  const doneCount = tasks.filter((t) => t.status === 'done').length;
  const contractStatus = data.contract?.status || 'none';
  const badge = CONTRACT_BADGE[contractStatus] || CONTRACT_BADGE.none;
  const isProspective = (data.status || '').toLowerCase() === 'prospective';

  return (
    <Card className="p-6 space-y-6" data-testid="agency-onboarding-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Rocket size={20} className="text-blue-600" /> Agency Onboarding
          </h3>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className={badge.cls} data-testid="contract-status-badge">{badge.label}</Badge>
            <Select value={data.status || 'Active'} onValueChange={changeStatus}>
              <SelectTrigger className="h-7 w-36 text-xs" data-testid="agency-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Prospective">Prospective</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">{doneCount}/{tasks.length} tasks complete</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setContractOpen(true)} data-testid="send-contract-btn">
            <FileSignature size={16} className="mr-2" /> Send Contract
          </Button>
          {isProspective && (
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={convertToActive} data-testid="convert-active-btn">
              <UserCheck size={16} className="mr-2" /> Convert to Active
            </Button>
          )}
        </div>
      </div>

      {/* Contract summary */}
      {contractStatus !== 'none' && (
        <div className="text-sm text-muted-foreground border rounded-lg p-3 bg-muted/20" data-testid="contract-summary">
          {contractStatus === 'sent' && <span>Contract sent to <strong>{data.primary_contact?.email}</strong> on {(data.contract?.sent_at || '').split('T')[0]} — awaiting signature.</span>}
          {contractStatus === 'signed' && <span className="text-green-700"><CheckCircle2 size={14} className="inline mr-1" />Contract signed on {(data.contract?.signed_at || '').split('T')[0]}. Filed in documents below.</span>}
        </div>
      )}

      {/* Tasks */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold flex items-center gap-2"><ClipboardList size={18} /> Onboarding Tasks</h4>
          <div className="flex items-center gap-2">
            {packs.length > 0 && (
              <div className="flex items-center gap-1">
                <Select value={applyPackId} onValueChange={setApplyPackId}>
                  <SelectTrigger className="h-9 w-48" data-testid="apply-pack-select"><SelectValue placeholder="Apply a checklist…" /></SelectTrigger>
                  <SelectContent>
                    {packs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={applyPack} disabled={!applyPackId} data-testid="apply-pack-btn">Apply</Button>
              </div>
            )}
            <Button size="sm" onClick={() => setTaskOpen(true)} data-testid="add-task-btn"><Plus size={16} className="mr-1" /> Task</Button>
          </div>
        </div>
        {tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tasks yet. Apply a checklist or add a task.</p>
        ) : (
          <div className="space-y-2">
            {tasks.map((t) => (
              <div key={t.id} className="flex items-start gap-3 border rounded-lg p-3" data-testid={`agency-task-${t.id}`}>
                <button onClick={() => toggleTask(t)} className="mt-0.5" data-testid={`toggle-task-${t.id}`}>
                  {t.status === 'done' ? <CheckCircle2 size={20} className="text-green-600" /> : <Circle size={20} className="text-muted-foreground" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${t.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{t.title}</div>
                  {t.description && <div className="text-sm text-muted-foreground">{t.description}</div>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                    {t.assignee_name && <span className="inline-flex items-center gap-1"><UserCheck size={12} /> {t.assignee_name}</span>}
                    {t.due_date && <span className="inline-flex items-center gap-1"><Clock size={12} /> Due {t.due_date}</span>}
                    {t.reminders_enabled && <Badge variant="outline" className="text-[10px]">Reminders on</Badge>}
                    {t.completed_by && <span>✓ by {resolveName(t.completed_by)}</span>}
                  </div>
                </div>
                <button onClick={() => deleteTask(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-task-${t.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold">Documents</h4>
          <label className="cursor-pointer">
            <input type="file" className="hidden" onChange={uploadDoc} data-testid="agency-doc-upload" />
            <span className="inline-flex items-center text-sm px-3 py-1.5 rounded-md border hover:bg-muted"><Upload size={14} className="mr-1" /> Upload</span>
          </label>
        </div>
        {(data.documents || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents yet. The signed contract will appear here automatically.</p>
        ) : (
          <div className="space-y-2">
            {data.documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`agency-doc-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{d.category} · {(d.uploaded_at || '').split('T')[0]}{d.uploaded_by ? ` · ${resolveName(d.uploaded_by)}` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)} data-testid={`download-doc-${d.id}`}><Download size={16} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteDoc(d.id)} data-testid={`delete-doc-${d.id}`}><Trash2 size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send Contract dialog */}
      <Dialog open={contractOpen} onOpenChange={setContractOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Send Contract for Signature</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Template</Label>
              <Select value={contractForm.template_id} onValueChange={(v) => setContractForm({ ...contractForm, template_id: v })}>
                <SelectTrigger data-testid="contract-template-select"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact name</Label><Input value={contractForm.contact_name} onChange={(e) => setContractForm({ ...contractForm, contact_name: e.target.value })} data-testid="contract-contact-name" /></div>
              <div><Label>Contact email</Label><Input type="email" value={contractForm.contact_email} onChange={(e) => setContractForm({ ...contractForm, contact_email: e.target.value })} data-testid="contract-contact-email" /></div>
            </div>
            <div><Label>Message (optional)</Label><Input value={contractForm.message} onChange={(e) => setContractForm({ ...contractForm, message: e.target.value })} placeholder="Please review and sign our partnership agreement." /></div>
            <div><Label>Contract expiration date (optional)</Label><Input type="date" value={contractForm.expires_at} onChange={(e) => setContractForm({ ...contractForm, expires_at: e.target.value })} data-testid="contract-expires" /></div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={contractForm.reminders_enabled} onCheckedChange={(v) => setContractForm({ ...contractForm, reminders_enabled: !!v })} data-testid="contract-reminders" />
              Send me reminders before this contract expires (30 & 7 days)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractOpen(false)}>Cancel</Button>
            <Button onClick={sendContract} disabled={sending} data-testid="contract-send-submit"><Send size={16} className="mr-2" />{sending ? 'Sending…' : 'Send Contract'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task dialog */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Onboarding Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="task-title" /></div>
            <div><Label>Description (optional)</Label><Input value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assign to</Label>
                <Select value={taskForm.assignee_user_id} onValueChange={(v) => setTaskForm({ ...taskForm, assignee_user_id: v })}>
                  <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Staff member" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{userName(u)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Due date</Label><Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} data-testid="task-due" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={taskForm.reminders_enabled} onCheckedChange={(v) => setTaskForm({ ...taskForm, reminders_enabled: !!v })} data-testid="task-reminders" />
              Email the assignee reminders as the due date approaches
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskOpen(false)}>Cancel</Button>
            <Button onClick={addTask} data-testid="task-submit">Add Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AgencyOnboarding;
